import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { ProviderFilters } from "@/components/ProviderFilters";
import { useAuth } from "@/context/AuthContext";
import { Star, MapPin, Lock, Sparkles, Search, BadgeCheck } from "lucide-react";
import { ResultsMap } from "@/components/ResultsMap";
import { trackSponsorClick } from "@/lib/sponsor";

export default function Artists() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const recommended = ["/vetrina", "/consigliati"].includes(useLocation().pathname);
  const [params] = useSearchParams();
  const initialPlace = params.get("lat") && params.get("lng") ? { name: params.get("comune") || "", prov: params.get("prov") || "", lat: Number(params.get("lat")), lng: Number(params.get("lng")) } : null;
  const [filters, setFilters] = useState(initialPlace ? { lat: initialPlace.lat, lng: initialPlace.lng } : {});
  const [place, setPlace] = useState(initialPlace);

  useEffect(() => {
    const url = recommended ? "/recommended" : "/artists";
    api.get(url, { params: recommended ? { track: true } : filters }).then((r)=>setItems(r.data.filter(x => recommended ? true : x.role==="artist")));
  }, [recommended, filters]);

  const filtered = (items || []).filter((a) => !q || (a.companyName||"").toLowerCase().includes(q.toLowerCase()) || (a.disciplines||[]).concat(a.macroCategory||[]).some(d=>d.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <span className="overline text-gold">{recommended?"Consigliati":"Directory artisti"}</span>
          <h1 className="font-display text-4xl font-bold mt-2">{recommended?"Consigliati dal Portale":"Artisti & Gruppi Spettacolo"}</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">{recommended?"Artisti e fornitori consigliati con media voto ≥ 3,5. Il portale ha mandato di vendita diretto.":"Esplora gli artisti. I prezzi sono visibili solo a Enti ed Organizzatori registrati."}</p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="artist-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cerca per disciplina o nome…" className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-full focus:outline-none focus:border-gold text-sm"/>
        </div>
      </div>

      {!recommended && <ProviderFilters initialPlace={initialPlace} onApply={(p, pl) => { setFilters(p); setPlace(pl); }} />}
      {!recommended && place && items && <ResultsMap place={place} items={filtered} />}

      {!user && (
        <div className="mb-8 p-4 rounded-xl border border-gold/30 bg-gold/5 flex items-center gap-3 text-sm">
          <Lock className="w-4 h-4 text-gold"/>
          <span>Stai navigando come anonimo. <Link to="/login" className="text-gold font-semibold underline">Accedi</Link> per vedere prezzi, contatti e recensioni.</span>
        </div>
      )}

      {items !== null && filtered.length===0 && <div className="card-premium p-8 text-center text-slate-500 text-sm" data-testid="no-results">Nessun artista corrisponde ai filtri selezionati.</div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((a)=>(
          <Link key={a.id} to={`/artista/${a.id}`} onClick={() => trackSponsorClick(a)} data-testid={`artist-card-${a.id}`} className="card-premium overflow-hidden group">
            <div className="aspect-[4/3] bg-navy relative">
              <img src={mediaUrl(a.media?.photos?.[0]) || "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=600"} alt={a.companyName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent"/>
              {a.sponsored ? <span data-testid={`sponsored-${a.id}`} className="chip-solid absolute top-4 left-4 !bg-copper !text-white"><Sparkles className="w-3 h-3"/>Sponsorizzato</span>
                : a.isRecommendedEvent && <span className="chip-solid absolute top-4 left-4"><Sparkles className="w-3 h-3"/>Consigliato</span>}
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="font-display text-xl font-bold leading-tight flex items-center gap-1.5">{a.companyName}{a.mepaEnabled && <span data-testid={`mepa-${a.id}`} className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0B5394] text-white shrink-0">MePA</span>}</div>
                <div className="text-xs text-slate-200 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/>{a.baseLocation}{a.distanceKm !== null && a.distanceKm !== undefined && <span data-testid={`distance-${a.id}`} className="ml-1 text-gold font-semibold">· a {a.distanceKm} km</span>}</div>
              </div>
            </div>
            <div className="p-5">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(a.disciplines?.length ? a.disciplines : [a.macroCategory]).filter(Boolean).slice(0,3).map((d,i)=><span key={i} className="chip-navy">{d}</span>)}
              </div>
              <div className="flex items-center justify-between text-sm">
                {a.rating!==null && a.rating!==undefined ? (
                  <span className="flex items-center gap-1 text-gold font-semibold"><Star className="w-4 h-4 fill-gold"/>{a.rating?.toFixed(1)} <span className="text-slate-400 font-normal">({a.reviewCount})</span></span>
                ) : <span className="text-slate-400 text-xs">Recensioni private</span>}
                {a.baseFeeNet!==null && a.baseFeeNet!==undefined ? (
                  <span className="font-display text-lg font-bold text-navy">€{a.baseFeeNet?.toFixed(0)}<span className="text-xs text-slate-500 font-body font-normal">/base</span></span>
                ) : (
                  <span className="chip-navy"><Lock className="w-3 h-3"/>Prezzo riservato</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
